import { Workspace } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { queryOptionsFindWorkspaceUsers } from '../../../../api/permissions/userApi';
import {
  WorkspaceContactFieldName,
  getWorkspaceOwnerContactDefaults,
} from '../../../../components/WorkspaceDetails/ContactDetails/contactDetailsConfig';
import { userIsWorkspaceOwner } from '../../../../utils/helpers/common';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsDependencyContext } from '../../shared/types';

interface ContactDefaultsContext extends WorkspaceDetailsDependencyContext {
  isOnboarding: boolean;
  workspace: Workspace;
}

/**
 * Onboarding only: seed every unassigned contact role with the workspace
 * owner, so a workspace leaves onboarding with contacts set instead of blank
 * (the submit mapper strips empty roles).
 *
 * Ports the legacy effect at
 * packages/frontend/src/components/WorkspaceDetails/ContactDetails/ContactDetailsForm.tsx:110-127.
 * The teammates query shares its key with `ContactRoleFields`, so this costs
 * no extra request.
 */
export const useContactDefaults = ({
  form,
  isOnboarding,
  workspace,
}: ContactDefaultsContext) => {
  const { getValues, setValue } = form;
  const { data: teammates } = useQuery({
    ...queryOptionsFindWorkspaceUsers(workspace._id),
    enabled: isOnboarding,
  });

  const workspaceOwnerId = teammates?.find((teammate) =>
    userIsWorkspaceOwner(teammate, workspace._id),
  )?._id;

  useEffect(() => {
    if (!isOnboarding || !workspaceOwnerId) return;

    const contactDefaults = getWorkspaceOwnerContactDefaults(
      getValues(WORKSPACE_DETAILS_FORM_FIELDS.contacts),
      workspaceOwnerId,
    );

    (
      Object.entries(contactDefaults) as [WorkspaceContactFieldName, string][]
    ).forEach(([fieldName, value]) => {
      // setValue leaves the field clean and untouched by default, which is what
      // this seed needs: it is not a user edit, and onboarding sends the
      // contacts section whether or not it is dirty.
      setValue(`${WORKSPACE_DETAILS_FORM_FIELDS.contacts}.${fieldName}`, value);
    });
  }, [getValues, isOnboarding, setValue, workspaceOwnerId]);
};
